import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import { appEntity } from '@/models/models'
import { ImageInterface } from '@/utils/image'
import { cloneDeep } from 'lodash'
import {
    MISSING_PASSWORD,
    MISSING_DD_PASSWORD,
    MISSING_DD_EMAIL,
    MISSING_EMAIL,
    MISSING_TEAM_NAME,
    MISSING_ACCOUNT_TYPE,
    MISSING_TEAM_ID,
    MISSING_BANNER,
    MISSING_ANDROID_TYPE,
    MISSING_API_KEY,
    MISSING_DEV_PERMISSIONS,
    MISSING_SPLASHSCREEN,
    MISSING_DEV_PROGRAM_REGISTERED_AT,
    MISSING_FASTLANE_PASSWORD,
    MISSING_DD_FASTLANE_PASSWORD,
    MISSING_CORPORATE_DOCUMENT,
    MISSING_CORPORATE_NAME,
    MISSING_CREATED_BY,
} from '@/utils/appUpdateErrorType'
import AppsApi from '@/api/apps'
import { getInputRuleFor, ScreenshotSize } from '@/utils/screenshot'

@Module({ namespaced: true })
export default class DetailModule extends VuexModule {
    public app: AppEntity = appEntity
    public images = new Map<string, ImageInterface>()
    public screenshots = new Map<string, ImageInterface[]>()
    public deleteScreenshots: number[] = []
    public isLoading: boolean = false

    @Mutation
    public setApp(app: AppEntity) {
        this.app = app
    }

    @Mutation
    public setLoading(loading: boolean) {
        this.isLoading = loading
    }

    @Mutation
    public setImageApp({ key, image }: any) {
        this.images.set(key, image)
    }

    @Mutation
    public addScreenshotApp({ size, image }: any) {
        if (this.screenshots.has(size)) {
            this.screenshots.get(size)?.push(image)
        } else {
            this.screenshots.set(size, [image])
        }
    }

    @Mutation
    public deleteScreenshotApp({ size, image }: any) {
        if (image[0] === 0) {
            const index = this.screenshots.get(size)?.indexOf(image[1]) ?? -1

            if (index > -1) {
                this.screenshots.get(size)?.splice(index, 1)

                if (this.screenshots.get(size)?.length === 0) {
                    this.screenshots.delete(size)
                }
            }
        } else {
            this.deleteScreenshots.push(image[0])
        }
    }

    @Mutation
    public clearImagesApp() {
        this.images.clear()
        this.screenshots.clear()
        this.deleteScreenshots = []
    }

    @Action
    public updateHomeApp(updatedApp: AppEntity) {
        this.context.dispatch('home/replaceApp', updatedApp, {root: true})
    }

    @Action
    public async getApp(id: number) {
        this.context.commit('setLoading', true)
        const response = await AppsApi.getApp(id)
        this.context.commit('setLoading', false)

        if (response.status === 'cancelled') {
            return null
        }

        if (response.status === 'success') {
            this.context.commit('setApp', response.data!.app)
        } else {
            alert(response.message)
        }

        this.context.commit('clearImagesApp')

        return (response.data && response.data.app) ? cloneDeep(response.data.app) : null
    }

    @Action
    public async saveApp(appParam: AppEntity): Promise<AppEntity | null> {
        const app = cloneDeep(appParam)

        app.errors = await this.context.dispatch('validate', app)

        if (app.errors != null && Object.keys(app.errors).length > 0) {
            return Promise.resolve(app)
        }

        this.context.commit('setLoading', true)

        const response = await (app.id > 0 ? AppsApi.putApp(app) : AppsApi.postApp(app))

        if (response.status !== 'success') {
            window.alert(response.message)
            this.context.commit('setLoading', false)
            return Promise.resolve(null)
        }

        if (response.data) {
            app.id = response.data.app.id
        }

        if (app.id > 0) {
            if (this.images.size > 0) {
                const uploadResponse = await AppsApi.uploadImagesApp(app.id, this.images)
                if (uploadResponse.status !== 'success') {
                    window.alert(uploadResponse.message)
                    this.context.commit('setLoading', false)
                    return Promise.resolve(null)
                }
            }

            if (app.iOS?.useCustomScreenshots) {
                if (this.deleteScreenshots.length > 0) {
                    const deleteResponse = await AppsApi.deleteScreenshotsApp(app.id, this.deleteScreenshots)
                    if (deleteResponse.status !== 'success') {
                        window.alert(deleteResponse.message)
                        this.context.commit('setLoading', false)
                        return Promise.resolve(null)
                    }
                }

                if (this.screenshots.size > 0) {
                    const uploadResponse = await AppsApi.uploadScreenshotsApp(app.id, this.screenshots)
                    if (uploadResponse.status !== 'success') {
                        window.alert(uploadResponse.message)
                        this.context.commit('setLoading', false)
                        return Promise.resolve(null)
                    }
                }
            }
        }

        this.context.commit('setLoading', false)
        this.context.commit('clearImagesApp')

        return Promise.resolve(app)
    }

    @Action
    public async requestUpdate(app: AppEntity): Promise<AppEntity | null> {
        const savedApp = await this.context.dispatch('saveApp', app)

        if (!savedApp || (savedApp.errors != null && Object.keys(savedApp.errors).length > 0)) {
            return Promise.resolve(savedApp)
        }

        const request = {
            android: app.android!.request,
            ios: app.iOS!.request,
        }

        const response = await AppsApi.requestAppUpdate(savedApp.id, request)

        if (response.status === 'success' && response.data?.app) {
            savedApp.android = response.data.app.android
            savedApp.iOS = response.data.app.iOS
            this.context.dispatch('updateHomeApp', savedApp)
            return Promise.resolve(savedApp)
        } else {
            return Promise.resolve(null)
        }
    }

    @Action
    public async searchBrandById(brandId: number): Promise<AppEntity | null> {
        this.context.commit('setLoading', true)
        const response = await AppsApi.getBrandById(brandId)
        this.context.commit('setLoading', false)

        if (response.status !== 'success') {
            return null
        }

        return response.data!.app
    }

    @Action
    public async deleteUpdate(appUpdate: AppUpdateEntity) {
        this.context.commit('setLoading', true)
        this.context.commit('setLoading', true, { root: true })
        const response = await AppsApi.deleteAppUpdateById(appUpdate.id)
        this.context.commit('setLoading', false)
        this.context.commit('setLoading', false, { root: true })

        return Promise.resolve(response.status === 'success')
    }

    @Action
    public async changePriority(appUpdate: AppUpdateEntity) {
        const response = await AppsApi.changeAppUpdatePriority(appUpdate.id)

        return Promise.resolve(response.status === 'success')
    }

    @Action
    private async validate(app: AppEntity) {
        let errors = {}

        if (app.name == null || app.name.length === 0) {
            errors = { ...errors, ...{ name: 'Nome não pode ser vazio' } }
        }

        if (app.brand == null || app.brand.id <= 0) {
            errors = { ...errors, ...{ brandId: 'Brand ID não pode ser vazio' } }
        }

        if (app.logo == null || app.logo.length === 0) {
            errors = { ...errors, ...{ logo: 'App precisa de logo' } }
        }

        if (app.errorList != null && (app.errorList.includes('legalterms') || app.errorList.includes('App sem termos e politicas'))) {
            errors = { ...errors, ...{ requirements: 'Requisitos não cumpridos' } }
        }

        errors = {
            ...errors,
            ...await this.context.dispatch('validateAndroid', app),
        }

        errors = {
            ...errors,
            ...await this.context.dispatch('validateIOS', app),
        }

        return Promise.resolve(errors)
    }

    @Action
    private validateAndroid(app: AppEntity) {
        const android = app.android
        const appAccount = app.account

        const shouldValidate = (app.validateOnlyRequested)
            ? android != null && android.request
            : android != null &&
                (android.request || appAccount?.androidType != null &&  appAccount?.androidType.length > 0)

        let errors = {}

        if (shouldValidate) {
            if (app.banner == null || app.banner.length === 0) {
                errors = { ...errors, ...{ [MISSING_BANNER]: 'App android precisa de banner' } }
            }

            if (appAccount != null) {
                if (appAccount.androidType == null || appAccount.androidType.length === 0) {
                    errors = { ...errors, ...{ [MISSING_ANDROID_TYPE]: 'App android precisa do tipo de conta' } }
                } else if (appAccount.androidType === 'CLIENT_ACCOUNT') {
                    if (appAccount.hasDevPermissions !== true) {
                        errors = { ...errors, ...{
                            [MISSING_DEV_PERMISSIONS]: 'Para criar o app android na conta do cliente é necessário que a conta DD tenha permissões de desenvolvedor',
                        }}
                    }

                    if (appAccount.apiKey == null || appAccount.apiKey.length === 0) {
                        errors = { ...errors, ...{ [MISSING_API_KEY]: 'Para criar o app android na conta do cliente é necessário a chave da API' } }
                    }
                }
            }
        }

        return Promise.resolve(errors)
    }

    @Action
    private validateIOS(app: AppEntity) {
        const iOS = app.iOS
        const appAccount = app.account
        const shouldValidate = (app.validateOnlyRequested)
            ? iOS != null && iOS.request
            : iOS != null &&
                (iOS.request || appAccount?.type != null &&  appAccount?.type.length > 0)

        let errors = {}

        if (shouldValidate) {
            if (app.splashscreen == null || app.splashscreen.length === 0) {
                errors = { ...errors, ...{ [MISSING_SPLASHSCREEN]: 'App iOS precisa de splashscreen' } }
            }

            if (appAccount != null) {
                const individualAccountRequiredFields = [
                    MISSING_TEAM_NAME,
                    MISSING_EMAIL,
                    MISSING_PASSWORD,
                    MISSING_TEAM_ID,
                    MISSING_FASTLANE_PASSWORD,
                    MISSING_ACCOUNT_TYPE,
                    MISSING_CREATED_BY,
                    MISSING_DEV_PROGRAM_REGISTERED_AT,
                ]

                const corporateAccountRequiredFields = [
                    MISSING_DD_EMAIL,
                    MISSING_DD_PASSWORD,
                    MISSING_DD_FASTLANE_PASSWORD,
                    MISSING_TEAM_NAME,
                    MISSING_TEAM_ID,
                    MISSING_ACCOUNT_TYPE,
                    MISSING_CREATED_BY,
                    MISSING_DEV_PROGRAM_REGISTERED_AT,
                    MISSING_CORPORATE_DOCUMENT,
                    MISSING_CORPORATE_NAME,
                ]

                const prop = Object.keys(appAccount)

                // If type is undefined or 'corporativa'
                if (appAccount!.type !== 'individual') {
                    for (const key of corporateAccountRequiredFields) {
                        if (!prop.includes(key)) {
                            errors = { ...errors, ...{ [key]: 'Campo não pode ser vazio' } }
                        }
                    }
                }

                // If type is undefined or 'individual'
                if (appAccount!.type !== 'corporativa') {
                    for (const key of individualAccountRequiredFields) {
                        if (!prop.includes(key)) {
                            errors = { ...errors, ...{ [key]: 'Campo não pode ser vazio' } }
                        }
                    }
                }

                if (iOS?.useCustomScreenshots) {
                    const iOSScreenshotSizes = [
                        ScreenshotSize.IOS_SIX_DOT_FIVE,
                        ScreenshotSize.IOS_FIVE_DOT_FIVE,
                        ScreenshotSize.IOS_TWELVE_DOT_NINE,
                    ]
                    const customScreenshots = iOS.customScreenshots ?? []

                    for (const iOSScreenshotSize of iOSScreenshotSizes) {
                        const fileCount = customScreenshots.filter((customScreenshot) => {
                            return customScreenshot.size === iOSScreenshotSize
                        }).length

                        if (fileCount === 0) {
                            errors = { ...errors, ...{ [iOSScreenshotSize]: 'Campo não pode ser vazio' } }
                        } else {
                            const inputRule = getInputRuleFor(iOSScreenshotSize)

                            if (inputRule.maximumNumber && inputRule.maximumNumber < fileCount) {
                                errors = { ...errors, ...{ [iOSScreenshotSize]: 'Campo excede o valor máximo de arquivos submetidos' } }
                            } else if (inputRule.minimumNumber && inputRule.minimumNumber > fileCount) {
                                errors = { ...errors, ...{ [iOSScreenshotSize]: 'Campo não cumpriu o valor mínimo de arquivos submetidos' } }
                            }
                        }
                    }
                }

                Object.entries(appAccount).forEach((entry) => {
                    const key = entry[0]
                    const value = entry[1]
                    const mandatory = corporateAccountRequiredFields.concat(individualAccountRequiredFields)

                    if (!mandatory.includes(key)) {
                        return
                    }

                    if (value == null || value.length === 0 || value === 0) {
                        if (
                            (appAccount!.type === 'corporativa' && corporateAccountRequiredFields.includes(key))
                            || (appAccount!.type === 'individual' && individualAccountRequiredFields.includes(key))
                            || ((appAccount!.type == null || appAccount.type.length === 0) && mandatory.includes(key))
                        ) {
                            errors = { ...errors, ...{ [key]: 'Campo não pode ser vazio' } }
                        }
                    }
                })
            }
        }

        return Promise.resolve(errors)
    }
}
